import { ELEMENT_GROUPS } from '@/components/sections/builder/builder-element';
import { createWithEqualityFn } from 'zustand/traditional';
import { devtools, persist } from 'zustand/middleware';
import { DEFAULT_THEME_ANIMATION } from '@/components/sections/builder/theme/animation';
import { DEFAULT_THEME_LAYOUT } from '@/components/sections/builder/theme/layout';
import { DEFAULT_THEME_DROPDOWN_POPUP } from '@/components/sections/builder/theme/dropdown-popup';
import { DEFAULT_THEME_SEARCH_BEHAVIOR } from '@/components/sections/builder/theme/search-behavior';
import { DEFAULT_THEME_PRODUCT_CARD } from '@/components/sections/builder/theme/product-card';

export type CommonElementDataType = {
  name: string;
  hidden?: boolean;
};

export type ElementType = {
  id: string;
  type: string;
  group: string;
  data?: unknown;
  parentData?: unknown;
  parentId?: string;
  theme?: ThemeType;
  childs?: ElementType[];
  themeId?: string;
  elements?: ElementType[];
};

export type ThemeLayoutType = {
  pageWidth: number;
  spaceSection: number;
  spaceGridVertical: number;
  spaceGridHorizontal: number;
};

export type ThemeAnimationType = {
  animateOnScroll: boolean;
  animationType: string;
  animationDelay: number;
  hoverEffect: string;
};

export type ThemeSearchBehaviorType = {
  enableSearchSuggestion: boolean;
  showProductVendor: boolean;
  showProductPrice: boolean;
};

export type ThemeDropdownPopupType = {
  borderWidth: number;
  borderOpacity: number;
  borderRadius: number;
  shadowOpacity: number;
  horizontalOffset: number;
  verticalOffset: number;
  shadowBlur: number;
};

export type ThemeButtonType = {
  borderThickness: number;
  borderOpacity: number;
  borderCornerRadius: number;
  shadowOpacity: number;
  shadowHorizontalOffset: number;
  sahdowVerticalOffset: number;
  shadowBlur: number;
  colorScheme?: ThemeColorSchemeType;
};

export type ThemeVariantPillType = {
  style: 'dropdown' | 'pills' | string;
  borderThickness: number;
  borderOpacity: number;
  borderCornerRadius: number;
  shadowOpacity: number;
  shadowHorizontalOffset: number;
  sahdowVerticalOffset: number;
  shadowBlur: number;
  colorScheme?: ThemeColorSchemeType;
};

export type ThemeInputType = {
  borderThickness: number;
  borderOpacity: number;
  borderCornerRadius: number;
  shadowOpacity: number;
  shadowHorizontalOffset: number;
  sahdowVerticalOffset: number;
  shadowBlur: number;
};

export type ThemeContentContainerType = {
  borderThickness: number;
  borderOpacity: number;
  borderCornerRadius: number;
  shadowOpacity: number;
  shadowHorizontalOffset: number;
  sahdowVerticalOffset: number;
  shadowBlur: number;
};

export type ThemeDrawerType = {
  borderThickness: number;
  borderOpacity: number;
  borderCornerRadius: number;
  shadowOpacity: number;
  shadowHorizontalOffset: number;
  sahdowVerticalOffset: number;
  shadowBlur: number;
  colorScheme: ThemeColorSchemeType;
};

export type ThemeCollectionType = {
  style: 'standard' | 'card' | string;
  imagePadding: number;
  textAlignment: 'left' | 'center' | 'right' | string;
  colorScheme?: ThemeColorSchemeType;
  borderThickness: number;
  borderOpacity: number;
  borderCornerRadius: number;
  shadowOpacity: number;
  shadowHorizontalOffset: number;
  sahdowVerticalOffset: number;
  shadowBlur: number;
  limitCharacters: number;
  fontSize: number;
};

export type ThemeMediaType = {
  borderThickness: number;
  borderOpacity: number;
  borderCornerRadius: number;
  shadowOpacity: number;
  shadowHorizontalOffset: number;
  sahdowVerticalOffset: number;
  shadowBlur: number;
};

export type ThemeProductCardType = {
  style: 'standard' | 'card' | string;
  imagePadding: number;
  textAlignment: 'left' | 'center' | 'right' | string;
  colorScheme?: ThemeColorSchemeType;
  borderThickness: number;
  borderOpacity: number;
  borderCornerRadius: number;
  shadowOpacity: number;
  shadowHorizontalOffset: number;
  sahdowVerticalOffset: number;
  shadowBlur: number;
  limitCharacters: number;
  variantPicker: 'dropdown' | 'pills' | string;
  showButtonOnHover: boolean;
  buttonTransparent: boolean;
  buttonPosition: 'inside' | 'outside' | string;
  buttonText: string;
  fontSizeTitle: number;
  fontSizePrice: number;
  positionButtonAddToCart: 'relative' | 'fixed' | string;
  showQtySold: boolean;
  qtySoldLabel: string;
  priceInformation: 'simple' | 'range' | string;
};

export type ThemeProductDetailButtonType = {
  showButtonBuyNow?: boolean;
  reverseButton?: boolean;
  buttonLabelAddToCart?: string;
  buttonLabelBuyNow?: string;
};

export type ThemeBlogCardType = {
  style: 'standard' | 'card' | string;
  imagePadding: number;
  textAlignment: 'left' | 'center' | 'right' | string;
  colorScheme?: ThemeColorSchemeType;
  borderThickness: number;
  borderOpacity: number;
  borderCornerRadius: number;
  shadowOpacity: number;
  shadowHorizontalOffset: number;
  sahdowVerticalOffset: number;
  shadowBlur: number;
  limitCharacters: number;
};

export type ThemeColorSchemeType = {
  id: number;
  background: string;
  bgGradient?: string;
  text?: string;
  textSecondary?: string;
  btnSolidBackgroud?: string;
  btnSolidlabel?: string;
  btnOutline?: string;
  shadow?: string;
  imageBackground?: string;
};

export type ThemeColorType = {
  colorSchemes: ThemeColorSchemeType[];
};

export type ThemeCartType = {
  cartType: 'drawer' | 'page' | 'popup_notification' | string;
  labelCheckout: string;
  labelContinueShopping: string;
  showProductVendor: boolean;
  enableCartNote: boolean;
  drawerColorScheme: ThemeColorSchemeType;
  fixedCheckoutButton: boolean;
  pageCartColorScheme: ThemeColorSchemeType;
};

export type ThemeBadgeType = {
  position: 'inside' | 'outside' | string;
  positionOnCard: 'bottom_left' | 'bottom_right' | 'top_left' | 'top_right' | string;
  borderCornerRadius: number;
  promotionText: string;
  promotionColorScheme: ThemeColorSchemeType;
  promotionColorGradient: {
    text: string;
    color1: string;
    color2: string;
  };
  soldOutText: string;
  soldOutColorScheme: ThemeColorSchemeType;
  newArrivalText: string;
  newArrivalColorScheme: ThemeColorSchemeType;
  newArrivalColorGradient: {
    text: string;
    color1: string;
    color2: string;
  };
  bestSellerText: string;
  bestSellerColorScheme: ThemeColorSchemeType;
  bestSellerColorGradient: {
    text: string;
    color1: string;
    color2: string;
  };
};

export type ThemeProductRating = {
  starColor: string;
};

export type ThemeTypographyType = {
  fontFamily?: string;
  fontScaleSize?: number;
  fontVariant?: string;
  headingFontFamily?: string;
  headingFontScaleSize?: number;
  headingFontVariant?: string;
};

export type ThemeBrandInformationType = {
  name: string;
  heading: string;
  subtext: string;
  logoUrl: string;
  logoWidth: number;
};

export type ThemePromotionType = {
  colorScheme: ThemeColorSchemeType;
  progressBar: string;
};

export type ThemeType = {
  layout?: ThemeLayoutType;
  animation?: ThemeAnimationType;
  searchBehavior?: ThemeSearchBehaviorType;
  dropdownPopup?: ThemeDropdownPopupType;
  button?: ThemeButtonType;
  variantPill?: ThemeVariantPillType;
  input?: ThemeInputType;
  contentContainer?: ThemeContentContainerType;
  drawer?: ThemeDrawerType;
  collection?: ThemeCollectionType;
  productCard?: ThemeProductCardType;
  productDetailButton?: ThemeProductDetailButtonType;
  blogCard?: ThemeBlogCardType;
  media?: ThemeMediaType;
  color?: ThemeColorType;
  cart?: ThemeCartType;
  badge?: ThemeBadgeType;
  colorSchemeModalDetail?: ThemeColorSchemeType;
  typography?: ThemeTypographyType;
  brandInformation?: ThemeBrandInformationType;
  promotion?: ThemePromotionType;
  productRating?: ThemeProductRating;
};

export type TPreviewPageitem = {
  id: string;
  title: string;
  slug: string;
};

type TCommons = {
  lastUpdatedAt: number;
  theme: ThemeType;
  elements: ElementType[];
  selectedId?: string;
  selectedParentId?: string;
  draggingId?: string;
  previewPageItem?: TPreviewPageitem;

  setTheme: (theme: ThemeType) => void;

  setElement: (elements: ElementType[]) => void;
  setDefault: (
    elements: ElementType[],
    theme: ThemeType,
    selectedId?: string,
    draggingId?: string,
    selectedParentId?: string
  ) => void;

  appendElement: <T>(
    element: { type: string; group: string; data?: T; childs?: ElementType[] },
    toId?: string,
    isBefore?: boolean
  ) => void;
  moveElement: (fromId: string, toId: string) => void;
  removeElement: (id: string) => void;
  setElementData: <T>(id: string, data: T) => void;

  appendChild: <T>(
    element: { parentId: string; type: string; group: string; data?: T },
    toId?: string,
    isBefore?: boolean
  ) => void;
  moveChild: (parentId: string, fromId: string, toId: string) => void;
  removeChild: (parentId: string, childId: string) => void;
  setChildData: <T>(parentId: string, childId: string, data: T) => void;

  setSelected: (id: string) => void;
  setSelectedParent: (id: string) => void;
  getSelected: () => ElementType | undefined;
  setDragging: (id: string) => void;
  clearDragging: () => void;
  isChildDragging: () => boolean;
  getGroupDragging: () => string;
  getTotalElementByTypes: (elementType: string) => number;
  getTotalChildByTypes: (parentId: string, childType: string) => number;
  clearSelected: () => void;
  clearSelectedParent: () => void;
  clear: () => void;

  setPreviewPageItem: (item: TPreviewPageitem) => void;
};

export const useBuilderStore = createWithEqualityFn<TCommons>()(
  devtools(
    persist(
      (set, get) => ({
        lastUpdatedAt: 0,
        theme: {
          layout: DEFAULT_THEME_LAYOUT,
          // animation: DEFAULT_THEME_ANIMATION,
          // dropdownPopup: DEFAULT_THEME_DROPDOWN_POPUP,
          // searchBehavior: DEFAULT_THEME_SEARCH_BEHAVIOR,
          // productCard: DEFAULT_THEME_PRODUCT_CARD,
          // productDetailButton: DEFAULT_THEME_PRODUCT_DETAIL_BUTTON,
        },
        elements: [],
        previewPageItem: {
          id: '',
          title: '',
          slug: '',
        },
        setPreviewPageItem: (item) => {
          set(() => ({ previewPageItem: item }));
        },
        setDefault: (elements, theme, selectedId, draggingId, selectedParentId) =>
          set(
            () => ({ elements, theme, selectedId, draggingId, selectedParentId }),
            false,
            'setDefault'
          ),

        setTheme: (theme) => {
          set(() => ({
            theme: {
              ...get().theme,
              ...theme,
            },
            lastUpdatedAt: new Date().getTime(),
          }));
        },

        setElement: (elements) => set(() => ({ elements }), false, 'setElement'),

        appendElement: (element, toId, isBefore) => {
          const id = `${new Date().getTime()}`;
          const newElement = {
            ...element,
            childs: element.childs?.map((child, i) => ({
              id: `${id}${new Date().getTime() + i + 1}`,
              type: child.type,
              group: child.group,
              data: child.data,
              parentId: id,
            })),
            id,
          };

          const groupIndexOf = (group: string) => ELEMENT_GROUPS.map((v) => v.group).indexOf(group);

          let newElements = [...(get().elements || []), newElement].sort((a, b) => {
            return groupIndexOf(a.group) - groupIndexOf(b.group);
          });

          if (toId) {
            newElements = newElements.flatMap((element) => {
              if (element.id === id) return [];
              if (element.id === toId) {
                if (isBefore) return [newElement, element];
                return [element, newElement];
              }
              return [element];
            });
          }

          set(
            () => ({
              elements: newElements || [],
              lastUpdatedAt: new Date().getTime(),
            }),
            false,
            'appendElement'
          );
        },
        moveElement: (fromId, toId) => {
          let newElements = get().elements || [];

          const currentElement = (get().elements || []).find((v) => v.id === fromId);
          const fromIndex = (get().elements || []).map((v) => v.id).indexOf(fromId);

          if (currentElement) {
            newElements = newElements.flatMap((element, toIndex) => {
              if (element.id === fromId) return [];
              if (element.id === toId) {
                if (fromIndex < toIndex) return [element, currentElement];
                return [currentElement, element];
              }
              return [element];
            });
          }

          set(
            () => ({
              elements: newElements || [],
              lastUpdatedAt: new Date().getTime(),
            }),
            false,
            'moveElement'
          );
        },
        removeElement: (id: string) => {
          const newElements = (get().elements || []).flatMap((element) => {
            if (element.id === id) return [];
            return [element];
          });

          set(
            () => ({
              elements: newElements || [],
              lastUpdatedAt: new Date().getTime(),
            }),
            false,
            'removeElement'
          );
        },
        setElementData: <T>(id: string, data: T) => {
          const newElements = (get().elements || []).map((prevElement) => {
            if (prevElement.id !== id) return prevElement;

            return {
              ...prevElement,
              data: {
                ...(prevElement.data || {}),
                ...data,
              },
            };
          });

          set(
            () => ({
              elements: newElements || [],
              lastUpdatedAt: new Date().getTime(),
            }),
            false,
            'setElementData'
          );
        },

        appendChild: (child, toId, isBefore) => {
          const newElements = (get().elements || []).map((prevElement) => {
            if (prevElement.id !== child.parentId || !prevElement.childs) return prevElement;
            const childs = prevElement.childs || [];

            const id = `${child.parentId}${new Date().getTime()}`;
            const newChild = { ...child, id };
            let newChilds = [...childs, newChild];

            if (toId) {
              newChilds = newChilds.flatMap((child) => {
                if (child.id === id) return [];
                if (child.id === toId) {
                  if (isBefore) return [newChild, child];
                  return [child, newChild];
                }
                return [child];
              });
            }

            return {
              ...prevElement,
              childs: newChilds,
            } as ElementType;
          });

          set(
            () => ({
              elements: newElements || [],
              lastUpdatedAt: new Date().getTime(),
            }),
            false,
            'appendChild'
          );
        },
        moveChild: (parentId, fromId, toId) => {
          const newElements = (get().elements || []).map((prevElement) => {
            if (prevElement.id !== parentId || !prevElement.childs) return prevElement;
            let newChilds = prevElement.childs || [];
            const currentChild = (prevElement.childs || []).find((v) => v.id === fromId);
            const fromIndex = (prevElement.childs || []).map((v) => v.id).indexOf(fromId);
            if (currentChild) {
              newChilds = newChilds.flatMap((child, toIndex) => {
                if (child.id === fromId) return [];
                if (child.id === toId) {
                  if (fromIndex < toIndex) return [child, currentChild];
                  return [currentChild, child];
                }
                return [child];
              });
            }

            return {
              ...prevElement,
              childs: newChilds,
            } as ElementType;
          });

          set(
            () => ({
              elements: newElements || [],
              lastUpdatedAt: new Date().getTime(),
            }),
            false,
            'moveChild'
          );
        },
        removeChild: (parentId: string, childId: string) => {
          const newElements = (get().elements || []).map((prevElement) => {
            if (prevElement.id !== parentId || !prevElement.childs) return prevElement;
            const newChilds = (prevElement.childs || []).flatMap((child) => {
              if (child.id === childId) return [];
              return [child];
            });

            return {
              ...prevElement,
              childs: newChilds,
            } as ElementType;
          });

          set(
            () => ({
              elements: newElements || [],
              lastUpdatedAt: new Date().getTime(),
            }),
            false,
            'moveChild'
          );
        },
        setChildData: <T>(elementId: string, childId: string, data: T) => {
          const newElements = (get().elements || []).map((prevElement) => {
            if (prevElement.id !== elementId || !prevElement.childs) return prevElement;

            return {
              ...prevElement,
              childs: (prevElement.childs || []).map((prevChild) => {
                if (prevChild.id !== childId) return prevChild;

                return {
                  ...prevChild,
                  data: {
                    ...(prevChild.data || {}),
                    ...data,
                  },
                };
              }),
            } as ElementType;
          });

          set(
            () => ({
              elements: newElements || [],
              lastUpdatedAt: new Date().getTime(),
            }),
            false,
            'setChildData'
          );
        },

        setSelected: (selectedId: string) => {
          set(() => ({ selectedId, lastUpdatedAt: new Date().getTime() }));
        },
        getSelected: () => {
          const current = (get().elements || [])
            .flatMap((v) => [v, ...(v.childs || [])])
            .find((v) => v.id === get().selectedId);

          return current;
        },
        clearSelected: () =>
          set(
            () => ({ selectedId: '', lastUpdatedAt: new Date().getTime() }),
            false,
            'clearSelected'
          ),

        setSelectedParent: (selectedParentId: string) => {
          set(
            () => ({ selectedParentId, lastUpdatedAt: new Date().getTime() }),
            false,
            'setSelectedParent'
          );
        },
        clearSelectedParent: () =>
          set(
            () => ({ selectedParentId: '', lastUpdatedAt: new Date().getTime() }),
            false,
            'clearSelectedParent'
          ),

        setDragging: (draggingId: string) => {
          set(() => ({ draggingId, lastUpdatedAt: new Date().getTime() }), false, 'setDragging');
        },
        isChildDragging: () => {
          return (
            (get().elements || [])
              .flatMap((element) => element.childs || [])
              .map((child) => child.id)
              .indexOf(get().draggingId as string) >= 0
          );
        },
        getGroupDragging: () => {
          return (get().elements || [])
            .flatMap((v) => [v, ...(v.childs || [])])
            .find((v) => v.id === get().draggingId)?.group as string;
        },
        getTotalElementByTypes: (elementType: string) => {
          return (get().elements || []).filter((v) => v.type === elementType).length;
        },
        getTotalChildByTypes: (parentId: string, childType: string) => {
          return (
            (get().elements || [])
              .find((v) => v.id === parentId)
              ?.childs?.filter((v) => v.type === childType)?.length || 0
          );
        },
        clearDragging: () => set(() => ({ draggingId: '' }), false, 'clearDragging'),
        clear: () => {
          set(
            () => ({
              lastUpdatedAt: 0,
              theme: {
                layout: DEFAULT_THEME_LAYOUT,
                animation: DEFAULT_THEME_ANIMATION,
                dropdownPopup: DEFAULT_THEME_DROPDOWN_POPUP,
                searchBehavior: DEFAULT_THEME_SEARCH_BEHAVIOR,
                productCard: DEFAULT_THEME_PRODUCT_CARD,
              },
              elements: [],
            }),
            false,
            'clear'
          );
        },
      }),
      {
        name: '@jstore-builder',
        version: 1,
      }
    )
  )
);
